import { useEffect } from 'react';
import { Building, Users, HardDrive, Bell, Infinity } from 'lucide-react';
import type { PlanStepProps } from '../types';

const GB_IN_BYTES = 1073741824;

export function LimitsStep({ data, onChange, onValidChange }: PlanStepProps) {
  useEffect(() => {
    onValidChange(true);
  }, [onValidChange]);

  // Convert bytes to GB for display
  const storageGb = Math.round(data.includedStorageBytes / GB_IN_BYTES);

  const limitConfigs = [
    {
      key: 'includedUnits' as const,
      label: 'Unidades',
      description: 'Número máximo de unidades/propiedades',
      icon: <Building className="w-5 h-5" />,
      presets: [5, 10, 25, 50, 100],
      value: data.includedUnits,
      onChange: (value: number) => onChange({ includedUnits: value }),
    },
    {
      key: 'includedUsers' as const,
      label: 'Usuarios',
      description: 'Número máximo de usuarios',
      icon: <Users className="w-5 h-5" />,
      presets: [3, 5, 10, 25, 50],
      value: data.includedUsers,
      onChange: (value: number) => onChange({ includedUsers: value }),
    },
    {
      key: 'includedNotifications' as const,
      label: 'Notificaciones',
      description: 'Número de notificaciones mensuales',
      icon: <Bell className="w-5 h-5" />,
      presets: [100, 500, 1000, 5000, 10000],
      value: data.includedNotifications,
      onChange: (value: number) => onChange({ includedNotifications: value }),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Límites del Plan
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Define los límites de recursos para este plan. Usa -1 para ilimitado.
        </p>
      </div>

      <div className="space-y-6">
        {limitConfigs.map((config) => {
          const isUnlimited = config.value === -1;

          return (
            <div key={config.key} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-2 bg-white dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300">
                  {config.icon}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800 dark:text-white">
                    {config.label}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {config.description}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`text-lg font-semibold ${
                    isUnlimited ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-white'
                  }`}>
                    {isUnlimited ? (
                      <span className="flex items-center gap-1">
                        <Infinity className="w-5 h-5" />
                        Ilimitado
                      </span>
                    ) : config.value.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={-1}
                  value={config.value}
                  onChange={(e) => config.onChange(parseInt(e.target.value) || 0)}
                  className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center
                           focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />

                <div className="flex-1 flex gap-1 flex-wrap">
                  {config.presets.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => config.onChange(preset)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                        config.value === preset
                          ? 'bg-brand-500 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      {preset.toLocaleString()}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => config.onChange(-1)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1 ${
                      isUnlimited
                        ? 'bg-emerald-500 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    <Infinity className="w-3 h-3" />
                    ∞
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {/* Storage - Special handling for bytes */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-2 bg-white dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300">
              <HardDrive className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-800 dark:text-white">
                Almacenamiento (GB)
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Espacio de almacenamiento en GB
              </p>
            </div>
            <div className="text-right">
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {storageGb} GB
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              value={storageGb}
              onChange={(e) => {
                const gb = parseInt(e.target.value) || 1;
                onChange({ includedStorageBytes: gb * GB_IN_BYTES });
              }}
              className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center
                       focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />

            <div className="flex-1 flex gap-1 flex-wrap">
              {[1, 5, 10, 50, 100].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => onChange({ includedStorageBytes: preset * GB_IN_BYTES })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    storageGb === preset
                      ? 'bg-brand-500 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {preset} GB
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Extra Prices Section */}
      <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
          Precios por Excedente
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Por Unidad Extra
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={data.extraUnitPrice}
                onChange={(e) => onChange({ extraUnitPrice: parseFloat(e.target.value) || 0 })}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Por Usuario Extra
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={data.extraUserPrice}
                onChange={(e) => onChange({ extraUserPrice: parseFloat(e.target.value) || 0 })}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Por GB Extra
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={data.extraStoragePricePerGb}
                onChange={(e) => onChange({ extraStoragePricePerGb: parseFloat(e.target.value) || 0 })}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Por Notificación Extra
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <input
                type="number"
                min={0}
                step={0.0001}
                value={data.extraNotificationPrice}
                onChange={(e) => onChange({ extraNotificationPrice: parseFloat(e.target.value) || 0 })}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
